import React from "react";
import { Helmet } from "react-helmet";

export default function BeaconTrackPage() {

  const features = [
    "GST Billing System",
    "Invoice Management",
    "Inventory Tracking",
    "Expense Management",
    "Customer Management",
    "Sales Reporting Dashboard",
    "Multi-User Access",
    "Cloud-Based Billing Software",
  ];

  const modules = [
    {
      title: "Invoice Management",
      desc: "Create professional invoices, manage billing records, and automate payment tracking efficiently.",
    },

    {
      title: "Inventory Management",
      desc: "Track stock levels, product movement, inventory updates, and warehouse operations in real time.",
    },

    {
      title: "GST Billing",
      desc: "Generate GST invoices, manage tax calculations, and simplify business accounting operations.",
    },

    {
      title: "Expense Tracking",
      desc: "Monitor business expenses, operational costs, and financial records from a centralized dashboard.",
    },

    {
      title: "Customer Management",
      desc: "Manage customer profiles, payment history, invoices, and communication details efficiently.",
    },

    {
      title: "Sales Analytics",
      desc: "Track sales performance, business growth, revenue insights, and financial reports in real time.",
    },
  ];

  const industries = [
    "Retail Stores",
    "Wholesale Businesses",
    "Rice Mills",
    "Manufacturing Companies",
    "Medical Shops",
    "Restaurants",
    "Distributors",
    "Local Businesses",
  ];

  const faqs = [
    {
      q: "What is Beacon Track Billing Software?",
      a: "Beacon Track is a cloud-based billing and inventory management software developed by SoftCoders for managing invoices, inventory, GST billing, expenses, and sales operations.",
    },

    {
      q: "Who can use Beacon Track?",
      a: "Retail stores, wholesalers, distributors, rice mills, manufacturers, restaurants, and local businesses can use Beacon Track Billing Software.",
    },

    {
      q: "Does Beacon Track support GST billing?",
      a: "Yes. Beacon Track supports GST invoice generation, tax calculations, billing records, and financial reporting.",
    },

    {
      q: "Is Beacon Track cloud-based?",
      a: "Yes. Beacon Track is a cloud-based billing software accessible from desktop and mobile devices.",
    },
  ];

  return (

    <div className="bg-[#070B1A] text-white overflow-hidden">

     
  <Helmet>

    {/* PRIMARY SEO */}
    <title>
      Beacon Track Billing Software | GST Billing & Inventory Management | SoftCoders
    </title>

    <meta
      name="description"
      content="Beacon Track is a cloud-based GST billing and inventory management software developed by SoftCoders for managing invoices, inventory, expenses, customer records, sales reporting, and business operations efficiently."
    />

    <meta
      name="keywords"
      content="
      billing software,
      gst billing software,
      inventory management software,
      billing software india,
      cloud billing software,
      invoice management software,
      gst invoice software,
      business billing software,
      inventory tracking software,
      retail billing software,
      wholesale billing software,
      manufacturing billing software,
      rice mill billing software,
      accounting software,
      expense management software,
      sales reporting software,
      customer management system,
      stock management software,
      cloud inventory software,
      online billing software,
      business management software,
      gst invoice generator,
      invoice software for businesses,
      retail inventory management,
      billing and inventory software,
      multi user billing software,
      billing software for shops,
      warehouse management software,
      purchase management software,
      sales analytics dashboard,
      crm billing software,
      business automation software,
      custom billing software,
      modern billing system,
      financial management software,
      smart billing software,
      inventory dashboard software,
      beacon track billing software,
      softcoders billing software,
      gst software india
      "
    />

    <meta name="author" content="SoftCoders" />
    <meta name="robots" content="index, follow" />
    <meta name="language" content="English" />
    <meta name="revisit-after" content="2 days" />

    {/* CANONICAL */}
    <link
      rel="canonical"
      href="https://softcoders.in/beacon-track-billing-software"
    />

    {/* OPEN GRAPH */}
    <meta
      property="og:title"
      content="Beacon Track Billing Software | GST Billing & Inventory Management | SoftCoders"
    />

    <meta
      property="og:description"
      content="Cloud-based billing and inventory management software for GST billing, invoices, stock tracking, expense management, and sales reporting."
    />

    <meta
      property="og:url"
      content="https://softcoders.in/beacon-track-billing-software"
    />

    <meta property="og:type" content="website" />

    <meta
      property="og:image"
      content="https://softcoders.in/og-beacon-track.jpg"
    />

    {/* TWITTER SEO */}
    <meta name="twitter:card" content="summary_large_image" />

    <meta
      name="twitter:title"
      content="Beacon Track Billing Software | GST Billing & Inventory Management | SoftCoders"
    />

    <meta
      name="twitter:description"
      content="Complete GST billing and inventory management software for retail stores, wholesalers, manufacturers, rice mills, and local businesses."
    />

    <meta
      name="twitter:image"
      content="https://softcoders.in/og-beacon-track.jpg"
    />

    {/* GEO SEO */}
    <meta name="geo.region" content="IN" />
    <meta name="geo.country" content="India" />

    {/* MOBILE SEO */}
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1"
    />

    <meta
      name="theme-color"
      content="#070B1A"
    />

    {/* PRODUCT SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Beacon Track Billing Software",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web-based",
        url: "https://softcoders.in/beacon-track-billing-software",
        image: "https://softcoders.in/og-beacon-track.jpg",
        description:
          "Beacon Track is a cloud-based billing and inventory management software developed by SoftCoders for managing invoices, GST billing, inventory, expenses, and business operations.",
        brand: {
          "@type": "Brand",
          name: "SoftCoders"
        },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "INR"
        },
        featureList: [
          "GST Billing System",
          "Invoice Management",
          "Inventory Tracking",
          "Expense Management",
          "Customer Management",
          "Sales Reporting Dashboard",
          "Cloud-Based Billing Software",
          "Multi-User Access"
        ]
      })}
    </script>

    {/* ORGANIZATION SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "SoftCoders",
        url: "https://softcoders.in",
        logo: "https://softcoders.in/logo.png",
        sameAs: [
          "https://www.facebook.com/",
          "https://www.instagram.com/",
          "https://www.linkedin.com/"
        ]
      })}
    </script>

    {/* FAQ SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Beacon Track Billing Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Beacon Track is a cloud-based billing and inventory management software developed by SoftCoders for managing invoices, inventory, GST billing, expenses, and sales operations."
            }
          },
          {
            "@type": "Question",
            name: "Who can use Beacon Track?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Retail stores, wholesalers, distributors, rice mills, manufacturers, restaurants, and local businesses can use Beacon Track Billing Software."
            }
          },
          {
            "@type": "Question",
            name: "Does Beacon Track support GST billing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. Beacon Track supports GST invoice generation, tax calculations, billing records, and financial reporting."
            }
          },
          {
            "@type": "Question",
            name: "Is Beacon Track cloud-based?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. Beacon Track is a cloud-based billing software accessible from desktop and mobile devices."
            }
          }
        ]
      })}
    </script>

  </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5 py-28">

        {/* GRID */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(99,102,241,.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99,102,241,.4) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />

        {/* GLOW */}
        <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full bg-indigo-600/20 blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <div className="max-w-5xl">

            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 text-indigo-300 text-sm font-medium">

              <div className="w-2 h-2 rounded-full bg-indigo-400"></div>

              Billing & Inventory Software

            </div>

            <h1 className="text-5xl lg:text-7xl font-black leading-[0.9] mt-8">

              Beacon Track
              <span className="block text-indigo-400 mt-2">
                Billing Software
              </span>

            </h1>

            <p className="text-slate-400 text-lg leading-8 mt-8 max-w-4xl">

              Beacon Track is a cloud-based billing and inventory management software
              developed by SoftCoders to simplify GST billing,
              invoice management,
              inventory tracking,
              expense management,
              and sales operations for modern businesses.

            </p>

            {/* BUTTONS */}
            <div className="flex flex-wrap gap-5 mt-10">

              <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="bg-indigo-600 hover:bg-indigo-500 transition-all duration-300 px-8 py-4 rounded-2xl font-bold shadow-[0_15px_40px_rgba(99,102,241,0.25)]">
                Request Demo
              </a>

              <a
    href="/contact"
 className="border border-white/10 hover:border-indigo-500/40 px-8 py-4 rounded-2xl text-slate-300 transition-all duration-300">
                Contact SoftCoders
              </a>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">

              {[
                ["GST", "Billing System"],
                ["24/7", "Cloud Access"],
                ["99.9%", "System Uptime"],
                ["Multi", "Business Support"],
              ].map(([num, label], i) => (

                <div key={i}>

                  <div className="text-4xl font-black text-indigo-400">
                    {num}
                  </div>

                  <div className="text-slate-500 mt-2">
                    {label}
                  </div>

                </div>

              ))}

            </div>

          </div>

        </div>

      </section>

      {/* CONTENT */}
      <section className="py-28 border-b border-white/5">

        <div className="max-w-5xl mx-auto px-6 lg:px-10">

          <h2 className="text-5xl font-black leading-tight">

            Complete Billing & Inventory Management Software

          </h2>

          <div className="space-y-8 text-slate-400 text-lg leading-9 mt-10">

            <p>

              Beacon Track Billing Software is designed for businesses
              looking to simplify billing,
              inventory management,
              GST invoicing,
              expense tracking,
              and sales reporting from a centralized dashboard.

            </p>

            <p>

              The software helps businesses manage invoices,
              customer records,
              stock management,
              purchase tracking,
              sales reports,
              and operational workflows efficiently.

            </p>

            <p>

              Beacon Track improves business productivity,
              reduces manual accounting work,
              simplifies financial management,
              and enables businesses to track operations in real time.

            </p>

            <p>

              The platform is optimized for performance,
              scalability,
              security,
              and mobile accessibility,
              making it suitable for modern retail,
              wholesale,
              and manufacturing businesses.

            </p>

          </div>

        </div>

      </section>

      {/* FEATURES */}
      <section className="py-28 border-b border-white/5">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="text-center max-w-3xl mx-auto">

            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">
              FEATURES
            </div>

            <h2 className="text-5xl font-black mt-6">

              Beacon Track Features

            </h2>

          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">

            {features.map((feature, i) => (

              <div
                key={i}
                className="
                  rounded-3xl
                  border
                  border-white/10
                  bg-[#0E142B]
                  p-8
                  hover:border-indigo-500/30
                  transition-all
                  duration-300
                "
              >

                <div className="text-indigo-400 text-2xl mb-6">
                  →
                </div>

                <h3 className="text-2xl font-bold">

                  {feature}

                </h3>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* MODULES */}
      <section className="relative py-28 border-b border-white/5 overflow-hidden">

        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 blur-3xl rounded-full"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <div className="max-w-4xl">

            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 text-indigo-300 text-sm font-medium">

              <div className="w-2 h-2 rounded-full bg-indigo-400"></div>

              Billing Software Modules

            </div>

            <h2 className="text-5xl lg:text-6xl font-black leading-[0.95] mt-8">

              Powerful Modules
              <span className="block text-indigo-400 mt-2">
                Built for Businesses
              </span>

            </h2>

          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">

            {modules.map((module, i) => (

              <div
                key={i}
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-[32px]
                  border
                  border-white/10
                  bg-[#0E142B]
                  p-8
                  hover:border-indigo-500/30
                  hover:-translate-y-2
                  transition-all
                  duration-500
                "
              >

                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 bg-gradient-to-br from-indigo-500/[0.08] to-transparent"></div>

                <div className="relative z-10">

                  <h3 className="text-2xl font-black group-hover:text-indigo-300 transition-all duration-300">

                    {module.title}

                  </h3>

                  <p className="text-slate-400 leading-8 mt-5">

                    {module.desc}

                  </p>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* INDUSTRIES */}
      <section className="py-28 border-b border-white/5">

        <div className="max-w-6xl mx-auto px-6 lg:px-10">

          <div className="text-center">

            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">
              INDUSTRIES
            </div>

            <h2 className="text-5xl font-black mt-6">

              Businesses Using Beacon Track

            </h2>

          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">

            {industries.map((item, i) => (

              <div
                key={i}
                className="
                  rounded-3xl
                  border
                  border-white/10
                  bg-[#0E142B]
                  p-8
                  text-center
                "
              >

                <div className="text-indigo-400 text-3xl mb-6">
                  ✓
                </div>

                <h3 className="text-2xl font-bold">

                  {item}

                </h3>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* FAQ */}
      <section className="py-28 border-b border-white/5">

        <div className="max-w-4xl mx-auto px-6 lg:px-10">

          <div className="text-center mb-20">

            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">
              FAQ
            </div>

            <h2 className="text-5xl font-black mt-6">

              Frequently Asked Questions

            </h2>

          </div>

          <div className="space-y-6">

            {faqs.map((faq, i) => (

              <div
                key={i}
                className="
                  rounded-3xl
                  border
                  border-white/10
                  bg-[#0E142B]
                  p-8
                "
              >

                <h3 className="text-2xl font-bold">

                  {faq.q}

                </h3>

                <p className="text-slate-400 leading-8 mt-4">

                  {faq.a}

                </p>

              </div>

            ))}

          </div>

        </div>

      </section>

    </div>

  );
}